/**
 * Which J&J team a question belongs to, opened from beside the routing
 * heading.
 *
 * Scoped to the choice *within* J&J Medical, which is what separates it from
 * the when-to-ask explainer, whose question is J&J versus a Primum expert. It
 * borrows that modal's shell deliberately: two modals on one screen asking
 * adjacent questions should differ in content, not in furniture.
 *
 * A table rather than a decision tree, because a tree walks someone to a
 * single answer and these routes are multi-select on purpose. It carries no
 * actions for the same reason: the boxes it explains sit directly behind it.
 */

import { X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { MobileSheetHeader } from '@/components/MedicalOnDemand/mobile/MobileSheetHeader';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { MOD_ROUTES, MOD_ROUTE_HELPER } from '@/constants/medicalOnDemand';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type RouteHelperModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const labelForRoute = (routeId: string): string =>
  MOD_ROUTES.find((route) => route.id === routeId)?.label ?? routeId;

/**
 * Two shapes, because stacking the desktop row does not produce a readable
 * mobile one.
 *
 * Desktop is a 2:1 split with a wide gutter, matching the explainer's example
 * table. The category carries a multi-sentence example and the destination is
 * one to three words, so an even split leaves the right column mostly empty
 * and the left one cramped.
 *
 * On desktop the destination sits at the top of the row, level with the
 * category, and the example hangs below it. Stacking that same DOM order puts
 * the example *between* the question type and its answer, so the answer lands
 * last, after three lines of example, in the same weight as the category — the
 * reader scans a wall of text to reach the one thing they opened this for.
 * Compact therefore reorders to category, destination, example, which is the
 * order a desktop reader's eye already takes, bolds the destination so
 * question and answer stop looking alike, and indents the example so it
 * reads as a subordinate aside rather than a peer of the category.
 *
 * The example stays under its category in both, rather than becoming a third
 * column: it is an instance of that category, and a column of its own would
 * claim the two are separate things to cross-reference.
 */
function HelperRow({
  asking,
  destination,
  isCompact,
}: {
  asking: string;
  destination: string;
  isCompact: boolean;
}) {
  // md (16px) on compact, one step above the destination's sm (14px): with
  // position no longer doing the desktop columns' job of telling category
  // and destination apart, identical type made them read as two peers
  // instead of heading-then-answer. Desktop keeps sm — its column already
  // separates them, so the same styling there is unambiguous as-is.
  const category = (
    <Text size={isCompact ? 'md' : 'sm'} className="font-semibold text-typography-900">
      {asking}
    </Text>
  );

  if (isCompact) {
    return (
      // Loose between the three parts, tight within the label/value pair, so
      // the pair reads as one unit instead of four equidistant lines.
      <VStack className="gap-3">
        {category}

        {/*
          One line, not label over value: that stack is the floating-label
          convention for an editable field, which this isn't, and it cost a
          full line per row against the sheet's limited height. Nested Text
          keeps "Send to" and the team inline and wrapping together.
          typography-600 rather than the lighter 500: accessibility.md's
          AA-normal cutoff on background-0 in light mode is 600, not 500,
          and this is still small/normal text at size="sm".
        */}
        <Text size="sm" className="text-typography-600">
          Send to: <Text className="font-semibold text-typography-900">{destination}</Text>
        </Text>
      </VStack>
    );
  }

  return (
    <VStack className="md:flex-row md:gap-10">
      <VStack className="gap-1 md:flex-[2]">{category}</VStack>
      <Text size="sm" className="font-semibold text-typography-900 md:flex-1">
        {destination}
      </Text>
    </VStack>
  );
}

/**
 * The intro qualifies the title ("not sure who to ask? you can ask more than
 * one"), so it sits in the header as its subtitle rather than as the first
 * line of the scrolling body. Below the divider it read as detached content,
 * and it pushed the table a full paragraph further down.
 */
function HelperIntro() {
  return (
    <Text size="sm" className="text-typography-700">
      {MOD_ROUTE_HELPER.intro}
    </Text>
  );
}

/**
 * Rows are ruled between each other rather than only under the header: a row
 * runs to several lines at both widths, and whitespace alone stops reading as
 * a boundary once it does. The last row has no rule, since there is nothing
 * below it to separate.
 */
function HelperBody({ isCompact }: { isCompact: boolean }) {
  return (
    <VStack>
      {/*
          Column headers only mean something once the row is actually two
          columns. Compact stacks instead, and carries the "Send to" cue per
          row, so a header here would label a structure that doesn't exist.
        */}
      {!isCompact && (
        <View className="border-b border-outline-300">
          <VStack className="pb-2 md:flex-row md:gap-10">
            <Text size="md" className="font-semibold text-typography-900 md:flex-[2]">
              Asking about
            </Text>
            <Text size="md" className="font-semibold text-typography-900 md:flex-1">
              Send to
            </Text>
          </VStack>
        </View>
      )}

      <VStack>
        {MOD_ROUTE_HELPER.rows.map((row, index) => (
          <View
            key={row.routeId}
            className={twJoin(
              'py-4',
              index < MOD_ROUTE_HELPER.rows.length - 1 && 'border-b border-outline-300'
            )}>
            <HelperRow
              asking={row.asking}
              destination={labelForRoute(row.routeId)}
              isCompact={isCompact}
            />
          </View>
        ))}
      </VStack>
    </VStack>
  );
}

export function RouteHelperModal({ isOpen, onClose }: RouteHelperModalProps) {
  const insets = useSafeAreaInsets();

  const isCompact = useIsCompactLayout();

  if (isCompact) {
    return (
      <Actionsheet isOpen={isOpen} onClose={onClose}>
        <ActionsheetBackdrop />
        {/*
          A flex-1 body needs both height bounds. With only a maximum the sheet
          renders translated a full viewport down, off screen entirely, so
          min and max are set to the same value rather than a bare height.
          Full height rather than the usual 60-85%: three rows with a
          multi-line example each don't fit an iPhone SE's ~470pt of body
          height at 85%, and a mostly-scrolled sheet reads as broken rather
          than as "more below." paddingTop clears the status bar/notch,
          which a partial-height sheet never reached.
        */}
        <ActionsheetContent
          testID="mod-route-helper-modal"
          className="w-full rounded-t-2xl p-0"
          style={{
            maxHeight: '100%',
            minHeight: '100%',
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
          }}>
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>

          <VStack className="w-full flex-1">
            <VStack className="gap-1 px-6 pb-4">
              <MobileSheetHeader
                title={MOD_ROUTE_HELPER.title}
                titleSize="h2-xl"
                onClose={onClose}
              />
              <HelperIntro />
            </VStack>

            <Divider className="flex-shrink-0" />

            <ActionsheetScrollView className="min-h-0 flex-1" contentContainerClassName="px-6 pb-6">
              <HelperBody isCompact />
            </ActionsheetScrollView>
          </VStack>
        </ActionsheetContent>
      </Actionsheet>
    );
  }

  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalBackdrop />

      {/*
        size="lg" caps at 640px; a row with a wrapping example wants more
        than that, so max-w overrides it up to 760px rather than adding a
        modal size the rest of the app has no other use for.
      */}
      <ModalContent testID="mod-route-helper-modal" className="max-h-[85vh] md:max-w-[760px]">
        <ModalHeader className="items-start">
          <VStack className="flex-1 gap-1">
            <Heading size="h2-xl">{MOD_ROUTE_HELPER.title}</Heading>
            <HelperIntro />
          </VStack>
          <ModalCloseButton hitSlop={ICON_BUTTON_HIT_SLOP} onPress={onClose}>
            <Icon as={X} size="md" className="text-typography-500" />
          </ModalCloseButton>
        </ModalHeader>

        {/* Matches ProductSupportGuidelines, the other MOD modal on this screen. */}
        <Divider className="my-4" />

        <ModalBody>
          <HelperBody isCompact={false} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
